// Constants
import { COMMON_FILE_EXTENSIONS } from '../../../../common/files/fileConstants';

// Utils
import getFileExtension from '../../../../common/files/getFileExtension';
import stripFileExtension from '../../../../common/files/stripFileExtension';
import randomString from '../../../../common/uid/randomString';

export const appendToFilename = (filename: string) => {
    const [base = '', ...rest] = filename.split('.');

    return [`${base}-${randomString(6)}`, ...rest].join('.');
};

/**
 * Replace or append an extension on a filename.
 *
 * `stripFileExtension` returns an empty string for a name with no extension, so append in that case rather
 * than dropping the name entirely.
 */
export const replaceFileExtension = (filename: string, extension: string): string => {
    const normalizedExtension = extension.replace(/^\./, '');
    const base = stripFileExtension(filename) || filename;

    return `${base}.${normalizedExtension}`;
};

/** `jpg` and `jpeg` are the same format, so neither should be rewritten into the other. */
const normaliseExtension = (extension: string): string =>
    extension === COMMON_FILE_EXTENSIONS.JPG ? COMMON_FILE_EXTENSIONS.JPEG : extension;

/** Take the extension from a mime type — the subtype, minus any parameters or structured suffix. */
const getMimeTypeExtension = (mimeType: string): string => {
    const [, subtype = ''] = mimeType.split(';')[0].split('/');

    return subtype.split('+')[0];
};

/**
 * Align a filename's extension with the actual type of the blob being uploaded.
 *
 * Edited images are named after the source image's URL, but an edit can change the format — cropping a
 * background-removed image re-encodes it as a transparent PNG even though the source URL ends `.jpeg`.
 * The uploaded filename becomes the S3 key and therefore the permanent render URL, so an extension that
 * disagrees with the bytes leaves the image served under a lying name for the rest of its life.
 *
 * A blob with no type is left alone — there is nothing better to go on.
 */
export const conformFilenameToBlobType = (filename: string, blob: Blob): string => {
    const extension = getMimeTypeExtension(blob.type);
    if (!extension || normaliseExtension(getFileExtension(filename)) === normaliseExtension(extension)) return filename;

    return replaceFileExtension(filename, extension);
};

type UploadFileBlob = Blob & { name: string; lastModified: number };

/**
 * Attach upload metadata expected by the image upload pipeline.
 */
export const enrichBlobAsUploadFile = (blob: Blob, filename: string): UploadFileBlob =>
    Object.assign(blob, {
        name: filename,
        lastModified: Date.now(),
    }) as UploadFileBlob;
